import axios from 'axios';

// Use environment variable or default to localhost in development
const API_URL =  'https://app.polarcut.io/api';

/**
 * Service to handle interactions with the clips metadata API
 */
class ClipMetadataService {
  /**
   * Get viral clips metadata for a specific job
   * @param {string} jobId - The ID of the job
   * @returns {Promise<Object>} Metadata for viral clips
   */
  static async getViralClipsMetadata(jobId) {
    try {
      const response = await axios.get(`${API_URL}/clips-meta/viral/${jobId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching viral clips metadata for ${jobId}:`, error);
      throw new Error(
        error.response?.data?.error ||
        'Failed to fetch viral clips metadata. Please try again.'
      );
    }
  }

  /**
   * Get all viral clips metadata files
   * @returns {Promise<Array>} List of metadata files
   */
  static async getAllViralClipsMetadata() {
    try {
      const response = await axios.get(`${API_URL}/clips-meta/viral`);
      return response.data;
    } catch (error) {
      console.error('Error fetching all viral clips metadata:', error);
      throw new Error(
        error.response?.data?.error ||
        'Failed to fetch viral clips metadata list. Please try again.'
      );
    }
  }

  /**
   * Get enhanced clips metadata for a specific video and chunk
   * @param {string} videoId - The ID of the video
   * @param {string} chunkId - The ID of the chunk (optional, defaults to '0')
   * @returns {Promise<Object>} Metadata for enhanced clips
   */
  static async getEnhancedClipsMetadata(videoId, chunkId = '0') {
    try {
      const response = await axios.get(`${API_URL}/clips-meta/enhanced/${videoId}/${chunkId}`);
      console.log("response.data", response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching enhanced clips metadata for video ${videoId} chunk ${chunkId}:`, error);
      throw new Error(
        error.response?.data?.error ||
        'Failed to fetch enhanced clips metadata. Please try again.'
      );
    }
  }

  /**
   * Get all available chunks for a specific video
   * @param {string} videoId - The ID of the video
   * @returns {Promise<Object>} Object containing list of chunk IDs
   */
  static async getVideoEnhancedClipsMetadata(videoId) {
    try {
      const response = await axios.get(`${API_URL}/clips-meta/enhanced/${videoId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching enhanced clips metadata for video ${videoId}:`, error);
      throw new Error(
        error.response?.data?.error ||
        'Failed to fetch enhanced clips metadata list. Please try again.'
      );
    }
  }

  /**
   * Check if metadata exists for a specific video/job ID
   * Returns the type of metadata available ('enhanced', 'viral', or null)
   * @param {string} id - The video/job ID to check
   * @returns {Promise<string|null>} The type of metadata available or null if none
   */
  static async checkMetadataExists(id) {
    try {
      // First check for enhanced metadata
      try {
        const enhancedMetadata = await this.getVideoEnhancedClipsMetadata(id);
        if (enhancedMetadata && enhancedMetadata.chunkIds && enhancedMetadata.chunkIds.length > 0) {
          return 'enhanced';
        }
      } catch (enhancedError) {
        // No enhanced metadata, try viral
      }
      
      // Then check for viral metadata
      try {
        const allViralMetadata = await this.getAllViralClipsMetadata();
        const metadataFiles = allViralMetadata.files || [];
        const hasViralMetadata = metadataFiles.some(file => file.replace('.json', '') === id);
        
        if (hasViralMetadata) {
          return 'viral';
        }
      } catch (viralError) {
        // No viral metadata either
      }
      
      // No metadata found
      return null;
    } catch (error) {
      console.error(`Error checking metadata for ${id}:`, error);
      return null;
    }
  }

  /**
   * Get enhanced clips metadata for a specific variation
   * @param {string} videoId - The video ID
   * @param {string} chunkId - The chunk ID
   * @param {string} variationId - The variation ID
   * @returns {Promise<Object>} - The enhanced clips metadata for the variation
   */
  async getEnhancedClipsMetadataForVariation(videoId, chunkId, variationId) {
    try {
      const response = await axios.get(`${API_URL}/clips-meta/enhanced/${videoId}/${chunkId}/variations/${variationId}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting enhanced clips metadata for variation ${variationId}:`, error);
      throw error;
    }
  }
}

export default ClipMetadataService; 